'use client';

import { getOrgId } from '@fe-common/local-ids/get-org-id';
import { trpc } from '@fe-common/trpc/trpc-client';
import { validateUrl } from '@fe-common/utils/url/validate-url';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { FloatLabel } from 'primereact/floatlabel';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Steps } from 'primereact/steps';
import { useState } from 'react';

interface CreateExperienceDialogProps {
  visible: boolean;
  onHide: () => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const CreateExperienceDialog = ({ visible, onHide, onSuccess, onError }: CreateExperienceDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [homepageURL, setHomepageURL] = useState('');
  const [firstClickHref, setFirstClickHref] = useState('');
  const [notes, setNotes] = useState('');

  const handleCreateServicePageExperiences = async () => {
    setIsLoading(true);

    try {
      const orgId = (await getOrgId())!;
      await trpc.rewrite.createServicePageExperiences.mutate({
        orgId,
        homepageURL,
        firstClickHref,
        notes: notes || undefined,
      });

      onHide();
      onSuccess?.();
    } catch (err) {
      const errorMessage = 'Failed to create service page experiences. Please try again.';
      onError?.(errorMessage);
      console.error('Error creating service page experiences:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset to default values
    setHomepageURL('');
    setFirstClickHref('');
    setNotes('');
    setActiveStep(0);
    onHide();
  };

  const canProceedFromStep = () => {
    if (activeStep === 0) {
      return validateUrl({ url: firstClickHref }) !== null;
    }
    if (activeStep === 1) {
      return validateUrl({ url: homepageURL }) !== null;
    }
    return true;
  };

  const handleNext = async () => {
    if (activeStep < 2) {
      setActiveStep((s) => s + 1);
      return;
    }
    await handleCreateServicePageExperiences();
  };

  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  const footer = (
    <div className="flex justify-end gap-2">
      {activeStep > 0 && (
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          onClick={handleBack}
          className="p-button-secondary"
          disabled={isLoading}
        />
      )}
      <Button
        label={activeStep < 2 ? 'Next' : 'Create'}
        icon={activeStep < 2 ? 'pi pi-arrow-right' : 'pi pi-check'}
        onClick={handleNext}
        className="p-button-primary"
        loading={isLoading}
        disabled={isLoading || !canProceedFromStep()}
      />
    </div>
  );

  const firstClickHasError = validateUrl({ url: firstClickHref }) === null;
  const homepageHasError = validateUrl({ url: homepageURL }) === null;
  const firstClickFullUrl = validateUrl({ url: firstClickHref }) ?? undefined;
  const homepageFullUrl = validateUrl({ url: homepageURL }) ?? undefined;

  return (
    <Dialog
      visible={visible}
      onHide={handleCancel}
      header="Increase Conversion Rate"
      footer={footer}
      modal
      style={{ width: '70%', maxWidth: '100%' }}
      contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
      pt={{
        mask: {
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(10px)',
          },
        },
      }}
    >
      <div className="space-y-4">
        <Steps
          model={[{ label: 'First Click' }, { label: 'Homepage' }, { label: 'Review' }]}
          activeIndex={activeStep}
          onSelect={(e) => setActiveStep(e.index)}
          readOnly={false}
        />

        {activeStep === 0 && (
          <div>
            <p className="text-gray-700">
              We&apos;ll increase conversion rate for prospects that have started interacting with your brand by
              visiting this landing page. Think of the conversion lift you could get from SEO, GEO, or PPC campaigns.
            </p>
            <div className="mt-4">
              <FloatLabel>
                <InputText
                  id="firstClickHref"
                  value={firstClickHref}
                  onChange={(e) => setFirstClickHref(e.target.value)}
                  className={`w-full${firstClickHasError ? ' p-invalid' : ''}`}
                  disabled={isLoading}
                />
                <label htmlFor="firstClickHref">First Click URL</label>
              </FloatLabel>
              {firstClickHasError && (
                <>
                  <small id="firstClickHref-error" className="p-error text-sm">
                    Please enter a valid URL.
                  </small>
                  <br />
                </>
              )}
              <small id="firstClickHref-help" className="text-gray-500 text-sm">
                Where customers go when they click an Ad, or find you via organic search (SEO/GEO).
              </small>
            </div>
          </div>
        )}

        {activeStep === 1 && (
          <div className="space-y-2">
            <p className="text-sm text-gray-700">Enter your site&apos;s homepage URL (e.g., https://example.com).</p>
            <br />
            <FloatLabel>
              <InputText
                id="homepageURL"
                value={homepageURL}
                onChange={(e) => setHomepageURL(e.target.value)}
                className={`w-full${homepageHasError ? ' p-invalid' : ''}`}
                disabled={isLoading}
              />
              <label htmlFor="homepageURL">Homepage URL</label>
            </FloatLabel>
            {homepageHasError && (
              <>
                <small id="homepageURL-error" className="p-error text-sm">
                  Please enter a valid URL.
                </small>
                <br />
              </>
            )}
            <div className="mt-4">
              <FloatLabel>
                <InputTextarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full"
                  disabled={isLoading}
                  rows={4}
                  autoResize
                />
                <label htmlFor="notes">Notes (Optional)</label>
              </FloatLabel>
              <small id="notes-help" className="text-gray-500 text-sm">
                Add any additional context or notes that should be considered when creating the experience.
              </small>
            </div>
          </div>
        )}

        {activeStep === 2 && (
          <div className="flex flex-column gap-3">
            <p>
              If a prospect first visits your brand by visiting{' '}
              {firstClickFullUrl ? (
                <a
                  href={firstClickFullUrl.fullUrl!}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--primary-color)', fontWeight: 600 }}
                >
                  {firstClickHref}
                </a>
              ) : (
                <strong style={{ color: 'var(--primary-color)' }}>your landing page</strong>
              )}{' '}
              via PPC or SEO, we&apos;ll optimize their visit to your{' '}
              {homepageFullUrl ? (
                <a
                  href={homepageFullUrl.fullUrl!}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--primary-color)', fontWeight: 600 }}
                >
                  {homepageURL}
                </a>
              ) : (
                <strong style={{ color: 'var(--primary-color)' }}>website</strong>
              )}{' '}
              to increase conversions—by rewriting content just for their persona and that visitor.
            </p>

            <Accordion multiple>
              <AccordionTab header="Does this still work if they close the browser or clear cookies?">
                <p>Their personalized experience continues on future visits; we don&apos;t rely solely on cookies.</p>
              </AccordionTab>
              <AccordionTab header="Will this impact SEO?">
                <p>
                  No. Personalization runs client-side for visitors. Your underlying, crawlable site remains unchanged.
                </p>
              </AccordionTab>
              <AccordionTab header="Can I turn this off or customize it?">
                <p>Yes. You can disable or adjust any experience at any time in the Experience Editor.</p>
              </AccordionTab>
            </Accordion>
          </div>
        )}
      </div>
    </Dialog>
  );
};
